import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { LWApiPublicNotificationDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/notifications/index.raw';
import ServerStatusBadge from 'components/ServerStatusBadge';
import ServerStatusBadgeFlat from 'components/ServerStatusBadgeFlat';
import type { AssetDetails } from 'modules/queries/asset/useDetails';

export interface CardIdentityProps extends BoxProps {
	assetDetails: AssetDetails;
	notification?: LWApiPublicNotificationDetailsResultRawI;
}

/**
 * Status badge, name, and primary address for a server list card. The badge
 * switches to its flat variant on narrow screens.
 */
const CardIdentity = ({
	assetDetails,
	notification,
	sx,
	...rest
}: CardIdentityProps) => {
	const {
		custom_name: customName,
		domain,
		ip,
		isPrivateParent,
		powerStatus,
		status,
	} = assetDetails;
	const { last_alert: lastAlert, description } = notification || {};

	return (
		<Box
			sx={combineSx(
				{
					display: 'flex',
					gap: 150,
					justifyContent: 'space-between',
					minWidth: 0,
					overflow: 'hidden',
					flexDirection: {
						xs: 'column',
						sm: 'row',
					},
					alignItems: {
						xs: 'start',
						sm: 'center',
					},
				},
				sx,
			)}
			{...rest}
		>
			<ServerStatusBadge
				status={status}
				powerStatus={powerStatus}
				notificationPrimary={description}
				notificationSecondary={lastAlert || ''}
				sx={{
					position: 'absolute',
					maxWidth: '100%',
					display: {
						xs: 'none',
						sm: 'inline-flex',
					},
				}}
			/>

			<ServerStatusBadgeFlat
				status={status}
				powerStatus={powerStatus}
				notificationPrimary={description}
				notificationSecondary={lastAlert || ''}
				sx={{
					display: {
						sm: 'none',
					},
					maxWidth: '100%',
				}}
			/>

			<Stack
				sx={{
					gap: 100,
					minWidth: 0,
					maxWidth: '100%',
					marginLeft: { sm: '40px' },
				}}
			>
				<TypographyOverflow
					sx={{
						fontWeight: 500,
						typography: { xs: 'label-xl', sm: 'label-lg' },
					}}
				>
					{customName || domain}
				</TypographyOverflow>

				{!isPrivateParent && (
					<TypographyOverflow
						variant="label-md"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						{ip}
					</TypographyOverflow>
				)}
			</Stack>
		</Box>
	);
};

export default CardIdentity;
